import React from 'react';
import {
  UserPermissions,
  SYSTEM_MODULES,
  ModulePermissions,
  AccessLevel,
  accessLevelToPermissions,
  permissionsToAccessLevel
} from '../../../types/permissions';
import './PermissionsEditor.css';

interface PermissionsEditorProps {
  permissions: Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'>;
  onChange: (permissions: Omit<UserPermissions, 'id' | 'usuario_id' | 'criado_em' | 'atualizado_em'>) => void;
  /** Lista de módulos ativos na empresa (ex: ['checklist_veiculo', 'conferencia_carga']) */
  activeEmpresaModules?: string[];
}

const PermissionsEditor: React.FC<PermissionsEditorProps> = ({ permissions, onChange, activeEmpresaModules }) => {
  // Filtrar módulos: só mostrar módulos que não exigem módulo empresa OU cujo módulo empresa está ativo
  const visibleModules = SYSTEM_MODULES.filter(module => {
    if (!module.requiredEmpresaModule) return true;
    if (!activeEmpresaModules) return true;
    return activeEmpresaModules.includes(module.requiredEmpresaModule);
  });

  const accessLevelOptions = [
    { value: AccessLevel.NONE, label: 'Sem acesso' },
    { value: AccessLevel.READ_ONLY, label: 'Somente leitura' },
    { value: AccessLevel.READ_CREATE, label: 'Leitura e cadastro' },
    { value: AccessLevel.READ_UPDATE, label: 'Leitura e edição' },
    { value: AccessLevel.READ_CREATE_UPDATE, label: 'Leitura, cadastro e edição' },
    { value: AccessLevel.FULL, label: 'Controle total' }
  ];

  const handleAccessToggle = (moduleKey: string) => {
    const accessKey = `acesso_${moduleKey}` as keyof typeof permissions;
    const permKey = `perm_${moduleKey}` as keyof typeof permissions;

    // Se as permissões do módulo não existirem, criar com valores padrão
    const currentPerms = (permissions[permKey] as ModulePermissions | undefined) ?? {
      read: false,
      create: false,
      update: false,
      delete: false
    };

    // Cast para boolean ao inverter o valor de acesso
    const currentAccess = (permissions[accessKey] as boolean | undefined) ?? false;

    onChange({
      ...permissions,
      [accessKey]: !currentAccess,
      [permKey]: currentPerms // Garantir que permissões existam
    });
  };

  const handleAccessLevelChange = (moduleKey: string, level: AccessLevel) => {
    const permKey = `perm_${moduleKey}` as keyof typeof permissions;
    const newPerms = accessLevelToPermissions(level);

    onChange({
      ...permissions,
      [permKey]: newPerms
    });
  };

  const handlePermissionToggle = (moduleKey: string, action: keyof ModulePermissions) => {
    const permKey = `perm_${moduleKey}` as keyof typeof permissions;

    // Se as permissões do módulo não existirem, criar com valores padrão
    const currentPerms = (permissions[permKey] as ModulePermissions | undefined) ?? {
      read: false,
      create: false,
      update: false,
      delete: false
    };

    onChange({
      ...permissions,
      [permKey]: {
        ...currentPerms,
        [action]: !currentPerms[action]
      }
    });
  };

  const handleSelectAllRead = () => {
    const updatedPermissions = { ...permissions };

    visibleModules.forEach(module => {
      const accessKey = `acesso_${module.key}` as keyof typeof permissions;
      const permKey = `perm_${module.key}` as keyof typeof permissions;

      updatedPermissions[accessKey] = true as any;
      updatedPermissions[permKey] = {
        read: true,
        create: false,
        update: false,
        delete: false
      } as any;
    });

    onChange(updatedPermissions);
  };

  const handleSelectAllFull = () => {
    const updatedPermissions = { ...permissions };

    visibleModules.forEach(module => {
      const accessKey = `acesso_${module.key}` as keyof typeof permissions;
      const permKey = `perm_${module.key}` as keyof typeof permissions;

      updatedPermissions[accessKey] = true as any;
      updatedPermissions[permKey] = {
        read: true,
        create: true,
        update: true,
        delete: true
      } as any;
    });

    onChange(updatedPermissions);
  };

  const handleClearAll = () => {
    const updatedPermissions = { ...permissions };

    visibleModules.forEach(module => {
      const accessKey = `acesso_${module.key}` as keyof typeof permissions;
      const permKey = `perm_${module.key}` as keyof typeof permissions;

      updatedPermissions[accessKey] = false as any;
      updatedPermissions[permKey] = {
        read: false,
        create: false,
        update: false,
        delete: false
      } as any;
    });

    onChange(updatedPermissions);
  };

  return (
    <div className="permissions-editor">
      <div className="permissions-header">
        <h3>Configuração de Permissões</h3>
        <div className="permissions-quick-actions">
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={handleSelectAllRead}
          >
            <i className="fas fa-eye"></i> Todos Leitura
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={handleSelectAllFull}
          >
            <i className="fas fa-check-double"></i> Todos Completo
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline"
            onClick={handleClearAll}
          >
            <i className="fas fa-times"></i> Limpar Todos
          </button>
        </div>
      </div>

      <div className="permissions-list">
        {visibleModules.map(module => {
          const accessKey = module.accessKey as keyof typeof permissions;
          const permKey = module.permKey as keyof typeof permissions;

          // Cast explícito para boolean e fallback para false se undefined
          const hasAccess = (permissions[accessKey] as boolean | undefined) ?? false;

          // Fallback para permissões padrão se o módulo não existir
          const modulePerms = (permissions[permKey] as ModulePermissions | undefined) ?? {
            read: false,
            create: false,
            update: false,
            delete: false
          };

          const currentLevel = permissionsToAccessLevel(modulePerms);

          return (
            <div key={module.key} className={`permission-module ${!hasAccess ? 'disabled' : ''}`}>
              <div className="module-header">
                <div className="module-info">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={hasAccess}
                      onChange={() => handleAccessToggle(module.key)}
                    />
                    <span className="checkbox-label">
                      <strong>{module.label}</strong>
                      <small>{module.description}</small>
                    </span>
                  </label>
                </div>

                {hasAccess && (
                  <div className="module-level-selector">
                    <select
                      className="form-control form-control-sm"
                      value={currentLevel}
                      onChange={(e) => handleAccessLevelChange(module.key, e.target.value as AccessLevel)}
                    >
                      {accessLevelOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {hasAccess && (
                <div className="module-permissions">
                  <label className="permission-item">
                    <input
                      type="checkbox"
                      checked={modulePerms.read}
                      onChange={() => handlePermissionToggle(module.key, 'read')}
                    />
                    <span>
                      <i className="fas fa-eye"></i> Leitura
                    </span>
                  </label>

                  <label className="permission-item">
                    <input
                      type="checkbox"
                      checked={modulePerms.create}
                      onChange={() => handlePermissionToggle(module.key, 'create')}
                      disabled={!modulePerms.read}
                    />
                    <span>
                      <i className="fas fa-plus"></i> Criar
                    </span>
                  </label>

                  <label className="permission-item">
                    <input
                      type="checkbox"
                      checked={modulePerms.update}
                      onChange={() => handlePermissionToggle(module.key, 'update')}
                      disabled={!modulePerms.read}
                    />
                    <span>
                      <i className="fas fa-edit"></i> Editar
                    </span>
                  </label>

                  <label className="permission-item">
                    <input
                      type="checkbox"
                      checked={modulePerms.delete}
                      onChange={() => handlePermissionToggle(module.key, 'delete')}
                      disabled={!modulePerms.read}
                    />
                    <span>
                      <i className="fas fa-trash"></i> Excluir
                    </span>
                  </label>
                </div>
              )}

              {module.requireAdmin && (
                <div className="module-warning">
                  <i className="fas fa-exclamation-triangle"></i>
                  <small>Módulo administrativo - requer cuidado ao conceder acesso</small>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PermissionsEditor;
